import type { RegisteredIconType } from "@/shared/icons/iconRegistry"
import { ROUTES } from "@/config/routes"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Static view IDs — views that don't require a route parameter.
 */
export type StaticViewId =
  | "today"
  | "evening"
  | "upcoming"
  | "someday"
  | "anyday"
  | "inbox"
  | "logbook"
  | "recurring"
  | "routines"
  | "trash"
  | "search"
  | "laterProjects"

/**
 * Dynamic view IDs — views that require a route parameter (container ID).
 */
export type DynamicViewId = "project" | "list" | "area" | "tag"

export type ViewId = StaticViewId | DynamicViewId

/**
 * Declarative view definition — metadata for UI rendering + TopNavBar.
 *
 * Query logic lives in useViewData; the registry is metadata only.
 * Adding a new view = adding a registry entry + a query case in useViewData.
 */
export interface ViewDefinition {
  id: ViewId
  label: string
  /** Icon from the icon registry. null = no standard icon (dynamic views use project/list-specific icons). */
  icon: RegisteredIconType | null
  /** Route path for static views, or route builder for dynamic views */
  route: string | ((id: string) => string)
  /** Empty state when no items match the view */
  emptyState: { title: string; subtitle: string }
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const VIEW_REGISTRY: Record<ViewId, ViewDefinition> = {
  today: {
    id: "today",
    label: "Today",
    icon: "today",
    route: ROUTES.TODAY,
    emptyState: {
      title: "All clear",
      subtitle: "No tasks or projects planned for today",
    },
  },
  evening: {
    id: "evening",
    label: "This Evening",
    icon: "today",
    route: ROUTES.EVENING,
    emptyState: {
      title: "Evenings free",
      subtitle: "No tasks or projects planned for this evening",
    },
  },
  inbox: {
    id: "inbox",
    label: "Inbox",
    icon: "inbox",
    route: ROUTES.INBOX,
    emptyState: {
      title: "Inbox zero",
      subtitle: "All tasks have been sorted",
    },
  },
  upcoming: {
    id: "upcoming",
    label: "Upcoming",
    icon: "upcoming",
    route: ROUTES.UPCOMING,
    emptyState: {
      title: "Nothing upcoming",
      subtitle: "No tasks scheduled for the future",
    },
  },
  anyday: {
    id: "anyday",
    label: "Anyday",
    icon: "anyday",
    route: ROUTES.ANYDAY,
    emptyState: {
      title: "No tasks",
      subtitle: "Tasks available any time will appear here",
    },
  },
  someday: {
    id: "someday",
    label: "Someday",
    icon: "someday",
    route: ROUTES.SOMEDAY,
    emptyState: {
      title: "Nothing deferred",
      subtitle: "Tasks you defer to someday will appear here",
    },
  },
  logbook: {
    id: "logbook",
    label: "Logbook",
    icon: "logbook",
    route: ROUTES.LOGBOOK,
    emptyState: {
      title: "Logbook empty",
      subtitle: "Completed and cancelled tasks will appear here",
    },
  },
  recurring: {
    id: "recurring",
    label: "Recurring",
    icon: "recurring",
    route: ROUTES.RECURRING,
    emptyState: {
      title: "No recurring tasks",
      subtitle: "Tasks with a repeat schedule will appear here",
    },
  },
  routines: {
    id: "routines",
    label: "Routines",
    icon: "routines",
    route: ROUTES.ROUTINES,
    emptyState: {
      title: "No routines yet",
      subtitle: "Chain recurring tasks into ordered sequences",
    },
  },
  trash: {
    id: "trash",
    label: "Trash",
    icon: "trash",
    route: ROUTES.TRASH,
    emptyState: {
      title: "Trash is empty",
      subtitle: "Deleted items are kept here for 30 days",
    },
  },
  search: {
    id: "search",
    label: "Search",
    icon: "search",
    route: ROUTES.SEARCH,
    emptyState: {
      title: "No results",
      subtitle: "Try a different search term",
    },
  },
  laterProjects: {
    id: "laterProjects",
    label: "Later Projects",
    icon: "laterProjects",
    route: ROUTES.LATER_PROJECTS,
    emptyState: {
      title: "No later projects",
      subtitle:
        "Projects deferred to someday or scheduled in the future will appear here",
    },
  },
  project: {
    id: "project",
    label: "Project",
    icon: null,
    route: ROUTES.PROJECT,
    emptyState: {
      title: "No tasks",
      subtitle: "Add a task to get started",
    },
  },
  list: {
    id: "list",
    label: "List",
    icon: null,
    route: ROUTES.LIST,
    emptyState: {
      title: "No items",
      subtitle: "Add an item to get started",
    },
  },
  area: {
    id: "area",
    label: "Area",
    icon: null,
    route: ROUTES.AREA,
    emptyState: {
      title: "Empty area",
      subtitle: "Projects and lists in this area will appear here",
    },
  },
  tag: {
    id: "tag",
    label: "Tag",
    icon: "tags",
    route: ROUTES.TAG,
    emptyState: {
      title: "No tasks",
      subtitle: "No active tasks with this tag",
    },
  },
} as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Resolve a view definition from a route pathname.
 * Handles both static paths ("/today") and dynamic paths ("/project/:id").
 */
export function getViewFromPathname(
  pathname: string,
): ViewDefinition | undefined {
  // Static routes — exact match
  for (const def of Object.values(VIEW_REGISTRY)) {
    if (typeof def.route === "string" && def.route === pathname) {
      return def
    }
  }

  // Dynamic routes — prefix match
  if (pathname.startsWith("/project/")) return VIEW_REGISTRY.project
  if (pathname.startsWith("/list/")) return VIEW_REGISTRY.list
  if (pathname.startsWith("/area/")) return VIEW_REGISTRY.area
  if (pathname.startsWith("/tag/")) return VIEW_REGISTRY.tag

  return undefined
}
